import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


export default function AddSpotModal({ onAdd, onClose }) {
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [difficulty, setDifficulty] = useState('all');
  const [waveType, setWaveType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [geoResults, setGeoResults] = useState([]);

  const verifyAddress = async () => {
    if (!address.trim() || address.trim().length < 3) return;
    setVerifying(true);
    setError('');
    setGeoResults([]);
    setVerified(false);
    try {
      const res = await authFetch(`${API}/api/spots/geocode`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: address.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.verified && data.results.length > 0) {
          setGeoResults(data.results);
          const first = data.results[0];
          setLat(String(first.lat));
          setLon(String(first.lon));
          setVerified(true);
        } else {
          setError(data.message || 'Address not found');
        }
      } else {
        setError('Verification failed');
      }
    } catch { setError('Network error'); }
    finally { setVerifying(false); }
  };

  const selectResult = (r) => {
    setLat(String(r.lat));
    setLon(String(r.lon));
    setAddress(r.display_name);
    setVerified(true);
    setGeoResults([]);
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Name required'); return; }
    if (!verified && !lat && !lon) { setError('Please verify the address first'); return; }
    setSubmitting(true);
    setError('');
    try {
      const body = { name: name.trim(), region: region.trim(), address: address.trim(), difficulty, wave_type: waveType.trim() };
      if (lat && lon) { body.lat = parseFloat(lat); body.lon = parseFloat(lon); }
      const res = await authFetch(`${API}/api/spots/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) { onAdd(); onClose(); }
      else { const d = await res.json(); setError(d.detail || 'Failed to add'); }
    } catch { setError('Network error'); }
    finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()} className="w-full max-w-sm bg-bg-paper border border-white/10 rounded-2xl p-5 shadow-2xl max-h-[85vh] overflow-y-auto" data-testid="add-spot-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold">Add a Spot</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Spot Name *</label>
            <input data-testid="add-spot-name" value={name} onChange={e => setName(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50" placeholder="e.g. Secret Cove" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Region</label>
            <input data-testid="add-spot-region" value={region} onChange={e => setRegion(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50" placeholder="e.g. San Diego, CA" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Address *</label>
            <div className="flex gap-1.5">
              <input data-testid="add-spot-address" value={address} onChange={e => { setAddress(e.target.value); setVerified(false); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); verifyAddress(); } }}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50" placeholder="e.g. 123 Coast Rd, San Diego, CA" />
              <button data-testid="verify-address-btn" type="button" onClick={verifyAddress}
                disabled={verifying || !address.trim() || address.trim().length < 3}
                className={`px-3 rounded-xl text-[10px] font-bold flex-shrink-0 transition-all ${verified ? 'bg-green-500/15 text-green-400 border border-green-500/20' : 'bg-primary/15 text-primary border border-primary/20 hover:bg-primary/20'} disabled:opacity-40`}>
                {verifying ? 'Verifying...' : verified ? 'Verified' : 'Verify'}
              </button>
            </div>
            {geoResults.length > 1 && (
              <div className="mt-1.5 space-y-1 max-h-28 overflow-y-auto" data-testid="geocode-results">
                <p className="text-[9px] text-muted-foreground/50">Multiple results — pick the right one:</p>
                {geoResults.map((r, i) => (
                  <button key={i} type="button" onClick={() => selectResult(r)} data-testid={`geocode-result-${i}`}
                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-[10px] transition-colors ${String(r.lat) === lat && String(r.lon) === lon ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-white/[0.03] text-muted-foreground hover:bg-white/5 border border-white/5'}`}>
                    <p className="truncate">{r.display_name}</p>
                    <p className="text-[8px] text-muted-foreground/40 mt-0.5">{r.lat.toFixed(4)}, {r.lon.toFixed(4)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1 block">Latitude {verified && <span className="text-green-400 text-[8px]">auto</span>}</label>
              <input data-testid="add-spot-lat" value={lat} onChange={e => setLat(e.target.value)} type="number" step="any" readOnly={verified}
                className={`w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50 ${verified ? 'text-green-400/80' : ''}`} placeholder="32.888" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1 block">Longitude {verified && <span className="text-green-400 text-[8px]">auto</span>}</label>
              <input data-testid="add-spot-lon" value={lon} onChange={e => setLon(e.target.value)} type="number" step="any" readOnly={verified}
                className={`w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50 ${verified ? 'text-green-400/80' : ''}`} placeholder="-117.253" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Difficulty</label>
              <select data-testid="add-spot-difficulty" value={difficulty} onChange={e => setDifficulty(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50 appearance-none">
                <option value="all">All levels</option><option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option><option value="advanced">Advanced</option><option value="expert">Expert</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Wave Type</label>
              <select data-testid="add-spot-wavetype" value={waveType} onChange={e => setWaveType(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50">
                <option value="">select</option><option value="beach break">Beach Break</option><option value="point break">Point Break</option>
                <option value="reef break">Reef Break</option><option value="river mouth">River Mouth</option>
                <option value="big wave">Big Wave</option><option value="barrel">Barrel</option><option value="slab">Slab</option><option value="other">Other</option>
              </select>
            </div>
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <button data-testid="add-spot-submit" onClick={handleSubmit} disabled={submitting || !name.trim()}
            className="w-full py-2.5 rounded-xl text-xs font-bold bg-primary text-black hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? 'Adding...' : 'Add Spot'}
          </button>
        </div>
        <p className="text-[9px] text-muted-foreground/40 text-center mt-3">Spots you add earn you Aura from community shakas</p>
      </motion.div>
    </motion.div>
  );
}
